import type { ReactNode } from 'react';
import { Alert, Box, Card, Heading, Image, Link, Stack, Text } from '@chakra-ui/react';
import { isBrowserPage, routeHref, routeState } from './route.ts';

// The parts the app's pages (settings, drivers, diagnostics, first start) are made of, in Chakra.

/**
 * A page's way back: to where it was opened from in this tab (the start page, the settings); else in
 * a browser to the start page, in an app to the dashboard.
 */
function BackLink() : ReactNode {
    const { from } = routeState();
    const home = isBrowserPage();
    const target = from ?? (home ? '/' : '/dash');
    const label = target === '/' ? '← Home' : target === '/dash' || target.startsWith('/dash/') ? '← Dashboard' : '← Back';

    return (
        <Link
            href={routeHref(target)}
            onClick={(event) => {
                if (from !== undefined && event.button === 0 && !event.metaKey && !event.ctrlKey) {
                    event.preventDefault();
                    history.back();
                }
            }}
            colorPalette="blue"
            alignSelf="flex-start"
        >
            {label}
        </Link>
    );
}

/** A page of the app: scrolls, at most about 52 characters' worth of form wide, with a way back. */
export function ChromePage({ title, back = true, children }: { title: string; back?: boolean; children: ReactNode }) : ReactNode {
    return (
        <Box className="chrome" height="100%" overflowY="auto" overscrollBehavior="none" bg="bg" color="fg">
            <Stack gap="4" p={{ base: '4', md: '6' }} maxW="3xl">
                {back ? (
                    <BackLink />
                ) : null}
                <Heading size="xl">{title}</Heading>
                {children}
            </Stack>
        </Box>
    );
}

/**
 * A page of one card in the middle of the screen, the app icon and the title at its top, and a line
 * under it (which server this is): signing in, a mailed link, a page that is not for this browser.
 */
export function CardPage({ title, note, children }: { title: string; note?: ReactNode; children: ReactNode }) : ReactNode {
    return (
        <Box className="chrome" height="100%" overflowY="auto" bg="bg" color="fg" display="flex" flexDirection="column" px="4" py={{ base: '6', md: '14' }}>
            <Stack gap="4" width="100%" maxW="md" mx="auto" my="auto" align="center">
                <Stack gap="5" width="100%" bg="bg.panel" borderWidth="1px" borderColor="casa.groupBorder" borderRadius="2xl" px={{ base: '5', md: '8' }} py={{ base: '6', md: '8' }}>
                    <Stack gap="3" align="center" textAlign="center">
                        <Image src="/app-icon.svg" alt="" boxSize="14" borderRadius="xl" />
                        <Heading size="xl">{title}</Heading>
                    </Stack>
                    {children}
                </Stack>
                {note ? (
                    <Text fontSize="xs" color="fg.muted">
                        {note}
                    </Text>
                ) : null}
            </Stack>
        </Box>
    );
}

/** A part of a page under its own heading. */
export function ChromeSection({ title, children }: { title: string; children: ReactNode }) : ReactNode {
    return (
        <Stack gap="2" mt="2">
            <Heading size="md">{title}</Heading>
            {children}
        </Stack>
    );
}

/** A card with a title, an explanation and what it holds. */
export function ChromeCard({ title, description, children }: { title?: string; description?: ReactNode; children?: ReactNode }) : ReactNode {
    return (
        <Card.Root variant="outline" size="sm">
            <Card.Body gap="3">
                {title ? <Card.Title fontSize="md">{title}</Card.Title> : null}
                {description ? <Text color="fg.muted" fontSize="sm">{description}</Text> : null}
                {children}
            </Card.Body>
        </Card.Root>
    );
}

/** What went wrong, kept with its line breaks (a dashboard's issues come one per line). */
export function ChromeError({ text }: { text: string }) : ReactNode {
    return (
        <Alert.Root status="error">
            <Alert.Indicator />
            <Alert.Description whiteSpace="pre-wrap" overflowWrap="anywhere">
                {text}
            </Alert.Description>
        </Alert.Root>
    );
}

/** Good news or a warning, in a line. */
export function ChromeNote({ status, text }: { status: 'success' | 'warning' | 'info'; text: ReactNode }) : ReactNode {
    return (
        <Alert.Root status={status} size="sm">
            <Alert.Indicator />
            <Alert.Description>{text}</Alert.Description>
        </Alert.Root>
    );
}
